/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { createContext, ReactNode, useContext, useMemo } from 'react';

export type AccountConfig = {
  recaptchaSiteKey?: string;
  showChinaTerms?: boolean;
};

type AccountConfigContextType = {
  config: AccountConfig;
};

export const AccountConfigContext = createContext<
  AccountConfigContextType | undefined
>(undefined);

type AccountConfigProviderProps = AccountConfig & {
  children: ReactNode;
};

export const AccountConfigProvider: React.FC<AccountConfigProviderProps> = ({
  children,
  ...config
}) => {
  // Only the tracked fields, so consumers never read a stale config.
  const value = useMemo(
    () => ({
      config: {
        recaptchaSiteKey: config.recaptchaSiteKey,
        showChinaTerms: config.showChinaTerms,
      },
    }),
    [config.recaptchaSiteKey, config.showChinaTerms],
  );

  return (
    <AccountConfigContext.Provider value={value}>
      {children}
    </AccountConfigContext.Provider>
  );
};

export const useAccountConfig = (): AccountConfigContextType => {
  return useContext(AccountConfigContext) ?? { config: {} };
};
